import { useState } from "react";
import { useSessao } from "@/sessao/useSessao";
import { useCriarContrato, usePlanos, type EscopoDeContrato } from "@/api/consultas";
import { FalhaDaApi, textoDoErro } from "@/api/problema";
import type { Plano } from "@/api/contrato";
import { Campo } from "@/componentes/Campo";
import { Selecao } from "@/componentes/Selecao";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { useAvisos } from "@/componentes/Avisos";
import { hojeUTC } from "@/calendario";
import { dinheiro } from "@/formato";

/** As opções de plano: só os disponíveis, porque o servidor recusa os outros. */
export function opcoesDePlano(
  planos: readonly Plano[],
  excluir?: string,
): (readonly [string, string])[] {
  return [
    ["", "—"],
    ...planos
      .filter((plano) => plano.disponivel && plano.id !== excluir)
      .map(
        (plano) =>
          [plano.id, `${plano.nome} · ${dinheiro(plano.preco_base_centavos)}/mês`] as const,
      ),
  ];
}

type CampoDoContrato = "plano_id" | "inicio" | "fim" | "dia_vencimento";

/** O campo que a recusa aponta, quando é um destes formulários de contrato. */
export function campoDoContrato(falha: unknown): CampoDoContrato | null {
  if (!(falha instanceof FalhaDaApi)) return null;
  const campo = falha.problema?.campo;
  return campo === "plano_id" || campo === "inicio" || campo === "fim" || campo === "dia_vencimento"
    ? campo
    : null;
}

/*
 * O dia de vencimento vai sempre no corpo, e nunca ausente para "o servidor
 * escolher": o operador vê o 10 preenchido e é esse que ele confirma. Até 28,
 * para existir em todo mês — a mesma faixa do `CHECK` da migração 043.
 */
const DIA_DE_VENCIMENTO_PADRAO = "10";

function diaDeVencimento(texto: string): number | null {
  if (!/^\d{1,2}$/.test(texto.trim())) return null;
  const valor = Number(texto.trim());
  return valor >= 1 && valor <= 28 ? valor : null;
}

/*
 * Contrato novo, para a aplicação (ou o cedente) sem contrato vigente.
 *
 * Com contrato vigente o caminho é a troca de plano, que encerra e abre na
 * mesma transação. Criar um segundo contrato por cima levaria
 * `409 contrato_sobreposto` — e o botão nem aparece nesse caso.
 */
export function NovoContrato({
  escopo,
  nome,
  aoFechar,
}: {
  escopo: EscopoDeContrato;
  /** De quem é o contrato: a aplicação ou a clínica. Vai sob o título. */
  nome: string;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const { data } = usePlanos(cliente, true);
  const criar = useCriarContrato(cliente, escopo);

  const [planoID, setPlanoID] = useState("");
  const [inicio, setInicio] = useState(hojeUTC);
  const [fim, setFim] = useState("");
  const [vencimento, setVencimento] = useState(DIA_DE_VENCIMENTO_PADRAO);
  const [erros, setErros] = useState<Partial<Record<CampoDoContrato, string>>>({});
  const [erroGeral, setErroGeral] = useState<string | undefined>(undefined);

  function enviar() {
    setErroGeral(undefined);
    if (planoID === "") {
      setErros({ plano_id: "Escolha o plano." });
      return;
    }
    const dia = diaDeVencimento(vencimento);
    if (dia === null) {
      setErros({ dia_vencimento: "De 1 a 28." });
      return;
    }
    setErros({});

    criar.mutate(
      { plano_id: planoID, inicio, ...(fim !== "" && { fim }), dia_vencimento: dia },
      {
        onSuccess: () => {
          avisos.feito("Contrato criado");
          aoFechar();
        },
        onError: (falha) => {
          const campo = campoDoContrato(falha);
          if (campo !== null) setErros({ [campo]: textoDoErro(falha) });
          else setErroGeral(textoDoErro(falha));
        },
      },
    );
  }

  return (
    <ModalDeCadastro
      aberto
      aoFechar={aoFechar}
      titulo="Novo contrato"
      descricao={nome}
      aoEnviar={enviar}
      enviando={criar.isPending}
      erro={erroGeral}
      rotuloDeEnvio="Criar contrato"
    >
      <Selecao
        rotulo="Plano"
        opcoes={opcoesDePlano(data?.planos ?? [])}
        value={planoID}
        onChange={(evento) => setPlanoID(evento.target.value)}
        erro={erros.plano_id}
        autoFocus
      />

      <div className="grid grid-cols-2 gap-3">
        <Campo
          rotulo="Início"
          type="date"
          required
          value={inicio}
          onChange={(evento) => setInicio(evento.target.value)}
          erro={erros.inicio}
        />
        <Campo
          rotulo="Fim"
          type="date"
          dica="Vazio: vigência aberta."
          value={fim}
          onChange={(evento) => setFim(evento.target.value)}
          erro={erros.fim}
        />
      </div>

      <Campo
        rotulo="Dia de vencimento"
        inputMode="numeric"
        dica="Dia do mês seguinte em que vence a fatura de cada competência."
        value={vencimento}
        onChange={(evento) => setVencimento(evento.target.value)}
        erro={erros.dia_vencimento}
        className="w-24"
      />
    </ModalDeCadastro>
  );
}
